import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Package, Check, X, Clock, RefreshCw, Send, Truck, Filter, Calendar, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

/**
 * Slice D3 — Per-section Inward Consignment clone (Salem-untouched).
 *
 * Literal-copy of pages/Sections/SectionIncomingConsignment.jsx with:
 *   • Props (sectionName / sectionDisplayName / backRoute) replaced by URL
 *     useParams(`section`) — same file serves color_combo / art_silk / kids.
 *   • The 3 consignment-path URLs swapped to the Phase D3 backend clone
 *     (/per-section/{slug}/inward-consignment/...).
 *   • The 3 internal-transfer-path URLs kept unchanged: those endpoints
 *     operate on the SHARED `internal_transfers` collection — they
 *     write nothing to any Salem-prefixed collection, so calling them
 *     from per-section UI is safe and avoids cloning shared logic.
 *
 * Salem's source file `SectionIncomingConsignment.jsx` is NOT imported,
 * NOT modified by this clone.
 */

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo Section',
  art_silk: 'Art Silk Section',
  kids: 'Kids Section',
};

const SectionIncomingConsignment = () => {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const sectionName = SLUG_TO_DISPLAY[slug] || slug;
  const sectionDisplayName = sectionName;
  const backRoute = `/sections/dispatch/${slug}`;
  const apiBase = `/per-section/${slug}/inward-consignment`;
  const [consignments, setConsignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectDialog, setRejectDialog] = useState({ open: false, entryId: null });
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(null);
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [sourceSiteFilter, setSourceSiteFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [sourceSites, setSourceSites] = useState([]);

  const displayName = sectionDisplayName || sectionName;

  const isReceived = (c) => ['received', 'accepted'].includes(c.status);
  const pendingList = consignments.filter((c) => !isReceived(c));
  const receivedList = consignments.filter(isReceived);
  const [activeTab, setActiveTab] = useState('pending');
  const visibleConsignments = activeTab === 'received' ? receivedList : pendingList;

  // Fetch unique source sites for the dropdown
  const fetchSourceSites = async () => {
    try {
      // Get unique sources from both consignments and internal transfers
      const response = await apiClient.get(`/per-section/${slug}/bundle-receipt/incoming`);
      const allConsignments = response.data.consignments || [];
      
      const sources = new Set();
      allConsignments.forEach(c => {
        if (c.vendor_name) sources.add(c.vendor_name);
        if (c.from_section) sources.add(c.from_section);
        if (c.source_site) sources.add(c.source_site);
      });
      
      setSourceSites(Array.from(sources).filter(Boolean).sort());
    } catch (error) {
      console.error('Error fetching source sites:', error);
    }
  };

  const fetchConsignments = async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params = new URLSearchParams();
      if (sourceSiteFilter) params.append('source_site', sourceSiteFilter);
      if (dateFromFilter) params.append('date_from', dateFromFilter);
      if (dateToFilter) params.append('date_to', dateToFilter);
      
      const queryString = params.toString();
      const url = `/per-section/${slug}/bundle-receipt/incoming${queryString ? '?' + queryString : ''}`;
      
      const response = await apiClient.get(url);
      
      const allConsignments = (response.data.consignments || []).map(c => ({
        ...c,
        source_type: c.type === 'internal_transfer' ? 'internal' : 'external',
        display_id: c.document_barcode || c.transfer_id || c.entry_id,
        // Ensure vendor_name is set properly
        vendor_name: c.vendor_name || (c.from_section ? `${c.from_section} Section` : 'Unknown')
      }));
      
      // Sort by date descending
      allConsignments.sort((a, b) => 
        new Date(b.created_at || b.documentation_confirmed_at || 0) - 
        new Date(a.created_at || a.documentation_confirmed_at || 0)
      );
      
      setConsignments(allConsignments);
    } catch (error) {
      toast.error('Failed to fetch consignments');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSourceSites();
  }, [sectionName]);

  useEffect(() => {
    fetchConsignments();
  }, [sectionName, sourceSiteFilter, dateFromFilter, dateToFilter]);

  const clearFilters = () => {
    setSourceSiteFilter('');
    setDateFromFilter('');
    setDateToFilter('');
  };

  const hasActiveFilters = sourceSiteFilter || dateFromFilter || dateToFilter;

  const handleRequest = async (item) => {
    try {
      setProcessing(item.entry_id);
      
      if (item.source_type === 'internal') {
        // Internal transfer - use request-consignment endpoint
        await apiClient.post('/internal-transfer/request-consignment', {
          transfer_id: item.transfer_id || item.entry_id
        });
        toast.success('Consignment requested - Source section notified to ship');
      } else {
        // External consignment - use existing endpoint
        await apiClient.post(`${apiBase}/${item.entry_id}/request`);
        toast.success('Consignment requested - waiting for shipment confirmation');
      }
      
      fetchConsignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request');
    } finally {
      setProcessing(null);
    }
  };

  const handleAccept = async (item) => {
    try {
      setProcessing(item.entry_id);

      if (item.source_type === 'internal') {
        // 2026-05: REWORK transfers fully retire the source bundle and push
        // its USNs into the Inhouse Rework Pool. Use the dedicated endpoint.
        if ((item.transfer_type || '').toUpperCase() === 'REWORK') {
          const tid = item.transfer_id || item.entry_id;
          const res = await apiClient.post(
            `/internal-transfer/${tid}/receive-for-rework`,
          );
          toast.success(
            res?.data?.message
            || `Rework received — ${res?.data?.usns_to_rework_pool ?? 0} USNs in pool.`
          );
        } else {
          // Standard internal transfer accept
          await apiClient.post('/internal-transfer/accept', {
            transfer_id: item.transfer_id || item.entry_id
          });
          toast.success('Transfer accepted - Ready for bundle receipt');
        }
      } else {
        // External consignment
        await apiClient.post(`${apiBase}/${item.entry_id}/accept`);
        toast.success('Consignment accepted - Ready for bundle receipt');
      }

      fetchConsignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      const item = consignments.find(c => c.entry_id === rejectDialog.entryId);
      setProcessing(rejectDialog.entryId);
      
      if (item?.source_type === 'internal') {
        // Internal transfer rejection
        await apiClient.post('/internal-transfer/reject', {
          transfer_id: item.transfer_id || rejectDialog.entryId,
          rejection_reason: rejectReason
        });
      } else {
        // External consignment rejection
        await apiClient.post(
          `${apiBase}/${rejectDialog.entryId}/reject`,
          null,
          { params: { reason: rejectReason } }
        );
      }
      
      toast.success('Consignment rejected');
      setRejectDialog({ open: false, entryId: null });
      setRejectReason('');
      fetchConsignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status) => {
    const normalized = (status || '').toString().toLowerCase();
    const statusConfig = {
      ready: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Ready - Not Requested' },
      created: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Ready - Not Requested' },
      requested: { color: 'bg-orange-100 text-orange-700', icon: Send, label: 'Requested - Awaiting Shipment' },
      shipped: { color: 'bg-blue-100 text-blue-700', icon: Truck, label: 'Shipped - Pending Receipt' },
      received: { color: 'bg-green-100 text-green-700', icon: Check, label: 'Received' },
      rejected: { color: 'bg-red-100 text-red-700', icon: X, label: 'Rejected' }
    };
    const config = statusConfig[normalized] || statusConfig.ready;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} border-0`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const renderActions = (consignment) => {
    // 2026-05-20 fix: normalize status casing — the per-section backend
    // historically returned uppercase ('READY', 'REQUESTED', 'SHIPPED') for
    // internal transfers while this handler only matched lowercase, leaving
    // the page with a "Ready - Not Requested" badge but NO action buttons.
    const status = (consignment.status || '').toString().toLowerCase();
    const entryId = consignment.entry_id;
    const isInternal = consignment.source_type === 'internal';
    
    if (status === 'ready' || status === 'created') {
      return (
        <div className="flex gap-3">
          <Button
            onClick={() => handleRequest(consignment)}
            disabled={processing === entryId}
            className="bg-orange-500 hover:bg-orange-600"
            data-testid={`request-${entryId}`}
          >
            <Send className="w-4 h-4 mr-2" />
            Request Consignment
          </Button>
          <Button
            variant="outline"
            onClick={() => setRejectDialog({ open: true, entryId })}
            disabled={processing === entryId}
            className="border-red-300 text-red-600 hover:bg-red-50"
            data-testid={`reject-${entryId}`}
          >
            <X className="w-4 h-4 mr-2" />
            Reject
          </Button>
        </div>
      );
    }
    
    if (status === 'requested') {
      return (
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-orange-600 border-orange-300">
            <Clock className="w-3 h-3 mr-1" />
            {isInternal ? 'Waiting for source section to ship' : 'Waiting for Inward to confirm shipment'}
          </Badge>
        </div>
      );
    }
    
    if (status === 'shipped') {
      const isRework = (consignment.transfer_type || '').toUpperCase() === 'REWORK';
      return (
        <div className="flex gap-3">
          <Button
            onClick={() => handleAccept(consignment)}
            disabled={processing === entryId}
            className={isRework ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#2D5F4F] hover:bg-[#1A1F2E]'}
            data-testid={`accept-${entryId}`}
          >
            <Check className="w-4 h-4 mr-2" />
            {isRework ? 'Receive for Rework' : 'Accept & Receive'}
          </Button>
          <Button
            variant="outline"
            onClick={() => setRejectDialog({ open: true, entryId })}
            disabled={processing === entryId}
            className="border-red-300 text-red-600 hover:bg-red-50"
            data-testid={`reject-${entryId}`}
          >
            <X className="w-4 h-4 mr-2" />
            Reject
          </Button>
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate(backRoute)} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Inward Consignment</h1>
              <p className="text-sm text-[#E8DCC8]">{displayName} Section</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={`border-white/20 text-white hover:bg-white/10 ${hasActiveFilters ? 'bg-white/20' : ''}`}
              data-testid="filter-button"
            >
              <Filter className={`w-4 h-4 mr-2`} />
              Filters {hasActiveFilters && `(${[sourceSiteFilter, dateFromFilter, dateToFilter].filter(Boolean).length})`}
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchConsignments}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="refresh-button"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-white border-b border-[#E5DFD0] shadow-sm">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Source Site Filter */}
              <div>
                <Label className="text-sm text-[#5A5F6E] mb-1 flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  Source Site / Vendor
                </Label>
                <Select value={sourceSiteFilter || "all"} onValueChange={(val) => setSourceSiteFilter(val === "all" ? "" : val)}>
                  <SelectTrigger className="w-full" data-testid="source-site-filter">
                    <SelectValue placeholder="All Sources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    {sourceSites.map((site) => (
                      <SelectItem key={site} value={site}>{site}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Date From Filter */}
              <div>
                <Label className="text-sm text-[#5A5F6E] mb-1 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  From Date
                </Label>
                <Input 
                  type="date" 
                  value={dateFromFilter}
                  onChange={(e) => setDateFromFilter(e.target.value)}
                  className="w-full"
                  data-testid="date-from-filter"
                />
              </div>
              
              {/* Date To Filter */}
              <div>
                <Label className="text-sm text-[#5A5F6E] mb-1 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  To Date
                </Label>
                <Input 
                  type="date" 
                  value={dateToFilter}
                  onChange={(e) => setDateToFilter(e.target.value)}
                  className="w-full"
                  data-testid="date-to-filter"
                />
              </div>
              
              {/* Clear Filters Button */}
              <div className="flex items-end">
                <Button 
                  variant="outline" 
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                  className="w-full"
                  data-testid="clear-filters-button"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              </div>
            </div>
            
            {/* Active Filters Summary */}
            {hasActiveFilters && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="text-sm text-[#5A5F6E]">Active filters:</span>
                {sourceSiteFilter && (
                  <Badge variant="secondary" className="text-xs">
                    Source: {sourceSiteFilter}
                    <button onClick={() => setSourceSiteFilter('')} className="ml-1 hover:text-red-500">×</button>
                  </Badge>
                )}
                {dateFromFilter && (
                  <Badge variant="secondary" className="text-xs">
                    From: {dateFromFilter}
                    <button onClick={() => setDateFromFilter('')} className="ml-1 hover:text-red-500">×</button>
                  </Badge>
                )}
                {dateToFilter && (
                  <Badge variant="secondary" className="text-xs">
                    To: {dateToFilter}
                    <button onClick={() => setDateToFilter('')} className="ml-1 hover:text-red-500">×</button>
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="flex items-center gap-2 mb-6 border-b border-[#E5DFD0]">
          <button
            onClick={() => setActiveTab('pending')}
            data-testid="tab-pending"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'pending' ? 'border-[#2D5F4F] text-[#2D5F4F]' : 'border-transparent text-[#5A5F6E] hover:text-[#1A1F2E]'}`}
          >
            Pending
            <span className={`ml-2 text-xs rounded-full px-2 py-0.5 ${activeTab === 'pending' ? 'bg-[#2D5F4F] text-white' : 'bg-gray-100 text-[#5A5F6E]'}`} data-testid="tab-pending-count">{pendingList.length}</span>
          </button>
          <button
            onClick={() => setActiveTab('received')}
            data-testid="tab-received"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'received' ? 'border-[#2D5F4F] text-[#2D5F4F]' : 'border-transparent text-[#5A5F6E] hover:text-[#1A1F2E]'}`}
          >
            Received Slips
            <span className={`ml-2 text-xs rounded-full px-2 py-0.5 ${activeTab === 'received' ? 'bg-[#2D5F4F] text-white' : 'bg-gray-100 text-[#5A5F6E]'}`} data-testid="tab-received-count">{receivedList.length}</span>
          </button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F]" />
          </div>
        ) : visibleConsignments.length === 0 ? (
          <Card className="border-[#E5DFD0]">
            <CardContent className="py-12 text-center">
              <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-[#1A1F2E] mb-2">
                {activeTab === 'received' ? 'No Received Slips' : 'No Pending Consignments'}
              </h3>
              <p className="text-[#5A5F6E]">
                {activeTab === 'received'
                  ? `No consignments have been received yet for ${displayName} section`
                  : `There are no pending consignments for ${displayName} section`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {visibleConsignments.map((consignment) => (
              <Card key={consignment.entry_id} className="border-[#E5DFD0]" data-testid={`consignment-${consignment.entry_id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-display">{consignment.document_barcode || consignment.entry_id}</CardTitle>
                    {getStatusBadge(consignment.status)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className={`grid grid-cols-2 ${consignment.usn_count != null ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-4 mb-4`}>
                    <div>
                      <p className="text-xs text-[#5A5F6E]">Entry ID</p>
                      <p className="font-medium text-[#1A1F2E]">{consignment.entry_id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#5A5F6E]">Bundles</p>
                      <p className="font-medium text-[#1A1F2E]" data-testid={`bundle-count-${consignment.entry_id}`}>
                        {consignment.bundle_count || consignment.bundles?.length || 0}
                      </p>
                    </div>
                    {consignment.usn_count != null && (
                      <div>
                        <p className="text-xs text-[#5A5F6E]">USNs</p>
                        <p className="font-medium text-[#1A1F2E]" data-testid={`usn-count-${consignment.entry_id}`}>
                          {consignment.usn_count}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-[#5A5F6E]">Vendor</p>
                      <p className="font-medium text-[#1A1F2E]">{consignment.vendor_name || consignment.source_section || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#5A5F6E]">
                        {consignment.status === 'shipped' ? 'Shipped At' : consignment.status === 'requested' ? 'Requested At' : 'Created At'}
                      </p>
                      <p className="font-medium text-[#1A1F2E]">
                        {consignment.shipped_at 
                          ? new Date(consignment.shipped_at).toLocaleString('en-IN')
                          : consignment.requested_at
                            ? new Date(consignment.requested_at).toLocaleString('en-IN')
                            : consignment.created_at
                              ? new Date(consignment.created_at).toLocaleString('en-IN')
                              : '-'}
                      </p>
                    </div>
                  </div>
                  
                  {renderActions(consignment)}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => !open && setRejectDialog({ open: false, entryId: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Consignment</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="reason">Rejection Reason</Label>
            <Input
              id="reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection..."
              className="mt-2"
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, entryId: null })}>
              Cancel
            </Button>
            <Button onClick={handleReject} className="bg-red-600 hover:bg-red-700" data-testid="confirm-reject-button">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SectionIncomingConsignment;
